import React from "react";
import useEntity from "../../../hooks/useTreeMarking";
import {
    Box, 
    Paper, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Link,
    TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import {ID} from "../../../../types/global.d";
import EntityListHeader from "./TreeMarkingListHeader";
import EntityListSearch from "./TreeMarkingListSearch";
import {useLocation} from "react-router";
import TreeMarkingListPagination from "./TreeMarkingListPagination";
import * as Api from "../../../apis/TreeMarking.api";
import { TreeMarking } from "../../../types/models/TreeMarking";
import { PrimaryTreeMarking } from "../../../types/models/TreeMarking";
import { usePagination } from '../../../../share';
import {displayDateTime, displayDate, displayTime} from "../../../../utils/formatDate.ts";
import {percentageFormat, fixedFormat, currencyFormat} from "../../../../utils/numberFormat";

import CuttingLink from "../../../../timber/components/cutting/CuttingDetail/CuttingLink";
import CuttingName from "../../../../timber/components/cutting/CuttingDetail/CuttingName";
import TimberStorageLink from "../../../../timber/components/timberStorage/TimberStorageDetail/TimberStorageLink";
import TimberStorageName from "../../../../timber/components/timberStorage/TimberStorageDetail/TimberStorageName";
import TimberTypeLink from "../../../../timber/components/timberType/TimberTypeDetail/TimberTypeLink";
import TimberTypeName from "../../../../timber/components/timberType/TimberTypeDetail/TimberTypeName";
import { toStringDisplay as toStringCuttingState } from "../../../types/models/CuttingState.d";
import { toStringDisplay as toStringShipmentState } from "../../../types/models/ShipmentState.d";
import { toStringDisplay as toStringShipmentType } from "../../../types/models/ShipmentType.d";
import { toStringDisplay as toStringUserType } from "../../../types/models/UserType.d";
import { toStringDisplay as toStringCordwoodMeasurementType } from "../../../types/models/CordwoodMeasurementType.d";

import { PrimaryCutting } from "../../../../timber/types/models/Cutting";
import { PrimaryTimberStorage } from "../../../../timber/types/models/TimberStorage";
import {useTheme} from "@mui/material/styles";
import useSearchDebounce from "../../../../hooks/useSearchDebounce";
import TreeMarkingListItemTools from "./TreeMarkingListItemTools";
import {useStateSession} from "../../../../hooks/useAuth";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans } from "@lingui/react/macro";
import {
    mdiLinkVariant,
} from "@mdi/js";
import {
    StyledFillNavLink,
    ToolbarTableCell,
    BigTableRow,
    BigTableHeaderRow,
    SmallTableRow,
    SmallTableHeaderRow,
    StyledTableCell,
    StyledMdiIcon,
} from "../../../../share/components/styled/Table";
import {OrderBy, OrderDirection} from "../../../../types/global.d";

type IProps = {
    showAdd: boolean,
    timberTypeId?: ID,
    volume?: number,
    cuttingId?: ID,
    timberStorageId?: ID,
    };

const TreeMarkingListItem = (props: IProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    
    const session = useStateSession()
    const {showAdd, ...query} = props;

    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('TreeMarking')

    
    
    
    
    const [, setSearchInputValue, searchInputTerm] = useSearchDebounce();
    const [searchInputAdvanced, setSearchInputAdvanced] = React.useState(undefined);

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    const { data: dataSimple = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
    }, 
        isLoading: isLoadingSimple,
        isFetching: isFetchingSimple,
        refetch: refetchSimple,
    } =
          Api.useGetTreeMarkingPresentationQuery({
                  list: { page, pageSize },
                  filter: {
                    cuttingIdRefs: (PrimaryCutting.fromQuery(query.cuttingId)) ? [ PrimaryCutting.fromQuery(query.cuttingId) ] : [],
                    storageIdRefs: (PrimaryTimberStorage.fromQuery(query.timberStorageId)) ? [ PrimaryTimberStorage.fromQuery(query.timberStorageId) ] : [],
                    // for now not supported to query here other types
                    timberTypeId: null,
                    // for now not supported to query here other types
                    volume: null,
                    orderBys,
                  },
              }, {skip: !!searchInputAdvanced})
    
    const { data: dataAdvanced = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
        }, 
        isLoading: isLoadingAdvanced,
        isFetching: isFetchingAdvanced,
        refetch: refetchAdvanced,
    } =
        Api.useGetTreeMarkingAdvancedPresentationQuery({
                list: { page, pageSize },
                filter: {

                    cuttingIdRefs: (PrimaryCutting.fromQuery(query.cuttingId)) ? [ PrimaryCutting.fromQuery(query.cuttingId) ] : [],
                    storageIdRefs: (PrimaryTimberStorage.fromQuery(query.timberStorageId)) ? [ PrimaryTimberStorage.fromQuery(query.timberStorageId) ] : [],
                    ...searchInputAdvanced,
                    orderBys,
                },
            }, {skip: !searchInputAdvanced})
    const {data, isLoading, isFetching, refetch} =
        searchInputAdvanced ? 
            {
                data: dataAdvanced,
                isLoading: isLoadingAdvanced,
                isFetching: isFetchingAdvanced,
                refetch: refetchAdvanced,
            } 
            :
        {
                data: dataSimple,
                isLoading: isLoadingSimple,
                isFetching: isFetchingSimple,
                refetch: refetchSimple,
            }

    const routeLocation = useLocation();
    
    const onClickUrl = (entity: TreeMarking) => routeLocation.pathname.endsWith('tree-markings') ?
        `${PrimaryTreeMarking.fromEntity(entity).toPath()}`
        :
        `tree-markings/${PrimaryTreeMarking.fromEntity(entity).toPath()}`

    const onEditUrl = (entity: TreeMarking) => `${onClickUrl(entity)}/edit`

    
    
    return React.useMemo(() => (
        <Box>
            <Paper>
                <EntityListHeader showAdd={showAdd} {...query}/>
                <EntityListSearch
                    setSearchInputValue={setSearchInputValue}
                    searchInputAdvanced={searchInputAdvanced} setSearchInputAdvanced={setSearchInputAdvanced}
                    query={query}
                    refetch={refetch}
                />
                <TableContainer>

                    


                    

                    

                    

                    
        
                    
                    {isSmallScreen ?
                        <Table stickyHeader data-testid="TreeMarking-entity-list" size="small">
                            <TableHead>
                                <SmallTableHeaderRow>
                                    
                            { query.timberTypeId ?
                                <></>
                                :
                            
                                <StyledTableCell align="left">
                                    
                                    <TableSortLabel
                                        active={ orderBys.find((item) => item.name === 'timberTypeId') }
                                        direction={ orderBys.find((item) => item.name === 'timberTypeId')?.direction || 'asc' }
                                        onClick={() => toggleOrder('timberTypeId')}
                                    >
                                    
                                        <Trans>Timber Type</Trans>
                                    
                                    </TableSortLabel>
                                    
                                </StyledTableCell>
                            }
                            
                        
                    
                                    
                                    
                            <StyledTableCell align="right">
                                
                                <TableSortLabel
                                    active={ orderBys.find((item) => item.name === 'volume') }
                                    direction={ orderBys.find((item) => item.name === 'volume')?.direction || 'asc' }
                                    onClick={() => toggleOrder('volume')}
                                >
                                
                                    <Trans>Volume (m&#xB3;)</Trans>
                                
                                </TableSortLabel>
                                
                            </StyledTableCell>
                        
                    
                                    
                                    
                                </SmallTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <SmallTableRow
                                        hover={true}
                                        key={ PrimaryTreeMarking.fromEntity(item).toPath() }
                                    >
                                        
                        
                        { query.timberTypeId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberTypeLink id={ item.timberTypeId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                        
                    
                        
                        <TableCell
                            scope="row"
                            align="right"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {fixedFormat(item.volume) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                        
                    
                        
                    
                                    </SmallTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        :
                    
                        <Table stickyHeader data-testid="TreeMarking-entity-list" size="small">
                            <TableHead>
                                <BigTableHeaderRow>
                                    
                                    
                                    
                                    
                                    
                                    { query.storageId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'storageId') }
                                                direction={ orderBys.find((item) => item.name === 'storageId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('storageId')}
                                            >
                                            
                                                <Trans>Timber Storage</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    { query.timberTypeId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'timberTypeId') }
                                                direction={ orderBys.find((item) => item.name === 'timberTypeId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('timberTypeId')}
                                            >
                                            
                                                <Trans>Timber Type</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="right">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'volume') }
                                            direction={ orderBys.find((item) => item.name === 'volume')?.direction || 'asc' }
                                            onClick={() => toggleOrder('volume')}
                                        >
                                        
                                            <Trans>Volume (m&#xB3;)</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryTreeMarking.fromEntity(item).toPath() }
                                    >
                                        
                                        
                                        
                                        
                                        
                        { query.storageId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberStorageLink id={ item.storageId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        { query.timberTypeId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberTypeLink id={ item.timberTypeId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="right"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {fixedFormat(item.volume) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        <ToolbarTableCell align="center" width="15%">
                                            <TreeMarkingListItemTools data={ item }
                                                                               onClickUrl={onClickUrl(item)} onEditUrl={onEditUrl(item)}
                                                                                />
                                        </ToolbarTableCell>
                                    </BigTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    }
                    
            
                    { !data.totalItems && !isLoading && !isFetching &&
                        <Typography
                            sx={ { flex: '1 1 100%', padding: "2em", } }
                            component="div"
                        >
                            <Trans>No Tree Markings</Trans>
                        </Typography>
                    }
                    { (isLoading || isFetching) &&
                        <GradientCircularProgress/>
                    }
                </TableContainer>
                <TreeMarkingListPagination
                    totalItems={ data.totalItems }
                    page={Number(page)}
                    pageSize={pageSize}
                    handleChangePage={handleChangePage}
                    handleChangePageSize={handleChangePageSize}
                />
            </Paper>
        </Box>
    ), [isLoading, isFetching,isSmallScreen, data, page, pageSize, onClickUrl,  onEditUrl])
}

export default TreeMarkingListItem;
